import { useEffect, useMemo, useRef, useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { AddCircleRounded, EditCalendar, ManageSearchOutlined, TextSnippetOutlined } from '@mui/icons-material'
import Document from '../document/Document'
import { IconButton } from '@mui/material'
import FabricationOrder from './FabricationOrder'
import PropTypes from 'prop-types'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from './partReferenceLabel'
import { SmartSelect } from '../../generic/smartSelect'
import MainImageThumbnail, { COLUMN_WIDTH as MAIN_IMAGE_COLUMN_WIDTH } from '../parts/MainImageThumbnail'
import useMainImageResolution from '../parts/useMainImageResolution'
import SensitiveBoundary from '../../vision/SensitiveBoundary'
// Import typedefs from another file
/**
 * @typedef {import('../../generic/utilities/objectStructures/orderWindow/order').Order} Order Order object
 * @typedef {import('../../generic/utilities/objectStructures/orderWindow/lineOrder').LineOrder} LineOrder LineOrder object
 */

/**
 * SmartSelect component
 * @param {Object} props Properties object
 * @param {Order} props.order Order object
 * @param {boolean} props.addModeByDefault Add mode by default
 */
// Historia 170 (J4b, S103) — `preselectedRecord` additive, optional, inert when absent (every OTHER
// caller): `Order.jsx` forwards this when a document repository row's "Open associated record"
// preselects an ORDER_LINE inside this order (`generic/SmartMateriaUITable.jsx`'s own prop, same
// shape).
const LineOrder = ({ order, addModeByDefault, preselectedRecord = null }) => {
  const [openFabricationOrderDialog, setOpenFabricationOrderDialog] = useState(false)
  const [editing, setEditing] = useState(false)
  /** @type {[LineOrder, React.Dispatch<SetStateAction<LineOrder>>]} */
  const [currentLineOrder, setCurrentLineOrder] = useState({})
  const [fabricationOrderAddModeByDefault, setFabricationOrderAddModeByDefault] = useState(false)
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261), delta del PO: the part reference reads in the installation's format,
  // through the same helper the request form uses. Painting and resolving with ONE format is what
  // keeps a chosen option findable (E4).
  const [frontEndConfig] = useFrontEndConfig()
  const partReferenceDisplayFormat = frontEndConfig?.partReferenceDisplayFormat
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [lineOrderRow, setLineOrderRow] = useState({})
  // Story 213 v2 (C1c): Sun may also flag the order in the header of its lines; the row alone is not the only signal
  const [headerSensitive, setHeaderSensitive] = useState(false)
  const [originalTargetDate, setOriginalTargetDate] = useState(null)
  const [originalDeliveryDate, setOriginalDeliveryDate] = useState(null)
  const [openDeliveryDateDialog, setOpenDeliveryDateDialog] = useState(false)
  const [deliveryDateDialogHandlers, setDeliveryDateDialogHandlers] = useState({ accept: () => {}, cancel: () => {} })
  const isUserAuthorized = useAuthorization()
  const canShowFabricationOrders = isUserAuthorized('SHOW_ORDERS_LINES_FABRICATION_ORDERS') && isUserAuthorized('FABRICATION ORDERS')
  const canAddFabricationOrders = isUserAuthorized('ADD_RECORD_FABRICATION_ORDERS')
  const editingRef = useRef(false)
  const [optionPartreferences, setOptionPartreferences] = useState([])
  const [partReferences, setPartReferences] = useState([])
  // Main image is resolved per visible page from the part reference id (S49), never from data
  // copied into the line. Re-resolving on each data fetch reflects the current image (S51 / S55).
  const [mainImageInfoMap, resolveMainImages, mergeMainImages, mainImageThumbnailMap] = useMainImageResolution()
  // Revision of historia 40 (#2819): the lines of an order whose status the profile does not own are
  // no longer restricted. S26's inherited gate is gone — the parent order's status only locks the
  // order's own `status` field (Order.jsx). Lines keep their own permissions
  // (EDIT/ADD/DELETE_ORDERS_LINES) untouched.

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const references = await apiGet('partReferencesShortData')
        setPartReferences(references)
        setOptionPartreferences(references.map(pr => formatPartReference(pr, partReferenceDisplayFormat)))
      } catch (err) {
        console.error('Error fetching part references:', err)
      }
    }
    fetchReferences()
  }, [partReferenceDisplayFormat])
  useEffect(() => {
    editingRef.current = editing
  }, [editing])

  const columns = useMemo(() => ([
    {
      name: t_('Line order'),
      field: 'lineNumber',
      mandatory: true,
      unique: true,
      numeric: true
    },
    {
      name: t_('Fabrication order'),
      field: 'fabricationOrderQuantity',
      readOnly: true,
      defaultNewValue: 0,
      sortable: false,
      search: false,
      render: (row) => {
        return (
          <>
            {row.fabricationOrderQuantity}
            <IconButton
              color='primary'
              onClick={() => {
                setCurrentLineOrder(row); setFabricationOrderAddModeByDefault(true); setOpenFabricationOrderDialog(true)
              }}
              disabled={(editingRef.current || row['smart-table-index'] <= -1) || !canAddFabricationOrders}
            >
              <AddCircleRounded />
            </IconButton>
          </>
        )
      }
    },
    {
      name: t_('Main image'),
      field: 'mainImage',
      width: MAIN_IMAGE_COLUMN_WIDTH,
      readOnly: true,
      disableFilter: true,
      search: false,
      render: (row) => {
        const partReferenceId = row?.partReference?.id
        if (partReferenceId === undefined || partReferenceId === null) {
          return null
        }
        return (
          <MainImageThumbnail
            partReferenceId={partReferenceId}
            mainImageInfo={mainImageInfoMap[partReferenceId] || {}}
            thumbnail={mainImageThumbnailMap[partReferenceId] || ''}
          />
        )
      }
    },
    {
      name: t_('Part Reference'),
      field: 'partReference.code',
      includeColumnFilters: ['partReference.name'],
      mandatory: true,
      readOnly: true,
      type: 'smartSelect',
      render: (row) => {
        if (row['smart-table-index'] === -1) {
          return (

            <div>
              <SmartSelect
                label={t_('Part Reference')}
                value={row.partReference?.code ? formatPartReference(row.partReference, partReferenceDisplayFormat) : null}
                onChange={(value) => {
                  const found = partReferences.find((r) => formatPartReference(r, partReferenceDisplayFormat) === value)
                  row.partReference = found
                  // Preview the picked reference's main image immediately, before saving (S49).
                  mergeMainImages([found?.id])
                }}
                selectableOptions={optionPartreferences}
                autoSelectFirst={false}
                disableClearable={false}
                minWidth='13rem'
                helperText=' '
              />
            </div>

          )
        }
        if (!row.partReference) {
          return ''
        }
        return (
          <>
            {formatPartReference(row?.partReference, partReferenceDisplayFormat)}
          </>
        )
      }
    },
    {
      // Single-valued: a line points to one part reference → one material type. `field` is the entity join
      // path so the header search filters lines server-side by material type (contains). Read-only/derived
      // and not sortable; `searchable` keeps the search box active despite sortable:false.
      name: t_('Material type'),
      field: 'partReference.materialType.name',
      readOnly: () => true,
      sortable: false,
      searchable: true,
      disableGenericSearch: true,
      render: (row) => systemValueLabel(t_, 'material_type', row.materialType || {})
    },
    {
      name: t_('Material class'),
      field: 'partReference.materialType.materialGroup.materialClass.name',
      readOnly: () => true,
      sortable: false,
      searchable: true,
      disableGenericSearch: true,
      render: (row) => systemValueLabel(t_, 'material_class', row.materialClass || {})
    },
    {
      name: t_('Quantity'),
      field: 'quantity',
      mandatory: true,
      numeric: true,
      defaultNewValue: 1
    },
    {
      name: t_('Target date'),
      field: 'targetDate',
      mandatory: true,
      type: 'dateTimePicker'
    },
    {
      name: t_('Delivery date'),
      field: 'deliveryDate',
      type: 'dateTimePicker',
      defaultNewValue: ''
    },
    {
      name: t_('Comment'),
      field: 'comment'
    },
    {
      name: t_('Forecast'),
      field: 'forecast',
      type: 'boolean',
      defaultNewValue: order.forecast,
      search: false,
      readOnly: true
    },
    {
      name: t_('Forecast Expir. Date'),
      field: 'expirationDate',
      type: 'dateTimePicker',
      defaultNewValue: order.expirationDate
    }
  ]), [partReferenceDisplayFormat,
    t_,
    canAddFabricationOrders,
    optionPartreferences,
    partReferences,
    mainImageInfoMap,
    mainImageThumbnailMap,
    mergeMainImages,
    order.forecast,
    order.expirationDate
  ])

  const actions = {
    edit: {
      fetchFunction: async (row) => {
        const targetDateChanged = originalTargetDate && new Date(row.targetDate).getTime() !== new Date(originalTargetDate).getTime()
        const deliveryDateChanged = (row.deliveryDate ?? '') !== (originalDeliveryDate ?? '')
        if (row.fabricationOrderQuantity > 0 && (targetDateChanged || deliveryDateChanged)) {
          const confirmed = await confirmDeliveryDateChange()
          return apiPut('order/' + order.id + '/lineOrder/' + row.id + '/' + confirmed, row)
        }
        return apiPut('order/' + order.id + '/lineOrder/' + row.id + '/false', row)
      },
      disabled: !isUserAuthorized('EDIT_ORDERS_LINES')
    },
    create: {
      fetchFunction: (row) => {
        return apiPost('order/' + order.id + '/lineOrder', row)
      },
      disabled: !isUserAuthorized('ADD_ORDERS_LINES')
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('order/lineOrder/' + rows[0].id)
        } else {
          return apiDelete('order/' + order.id + '/lineOrder/multi', rows)
        }
      },
      disabled: !isUserAuthorized('DELETE_ORDERS_LINES')
    },
    custom: [
      {
        name: 'View Fabrication Orders',
        render: (row) => {
          return (
            <IconButton>
              <ManageSearchOutlined />
            </IconButton>
          )
        },
        disabled: editing || !canShowFabricationOrders,
        onClick: (row) => {
          setCurrentLineOrder(row)
          setFabricationOrderAddModeByDefault(false)
          setOpenFabricationOrderDialog(true)
        }
      },
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const data = { id: row.id, code: row.lineNumber, name: row.lineNumber }
          setLineOrderRow(data)
          setShowDocument(true)
        },
        disabled: editing || !isUserAuthorized('DOCUMENTS_ORDER_LINE')

      }

    ]
  }

  const dataFetch = async (requestPagination) => {
    const data = await apiGet('order/' + order.id + '/lineOrders', requestPagination)
    const rows = Array.isArray(data) ? data : (data?.content || [])
    resolveMainImages(rows.map((row) => row?.partReference?.id))
    setHeaderSensitive(!Array.isArray(data) && data?.sensitive === true)
    return data
  }
  const confirmDeliveryDateChange = () => {
    return new Promise((resolve) => {
      const handleAccept = () => {
        setOpenDeliveryDateDialog(false)
        resolve(true)
      }
      const handleCancel = () => {
        setOpenDeliveryDateDialog(false)
        resolve(false)
      }

      setDeliveryDateDialogHandlers({ accept: handleAccept, cancel: handleCancel })
      setOpenDeliveryDateDialog(true)
    })
  }

  return (
    <SensitiveBoundary sensitive={order.sensitive === true || headerSensitive}>
      <SmartDialog
        setOpen={openFabricationOrderDialog}
        message=''
        renderComponent={<FabricationOrder order={order} client={order.client} lineOrder={currentLineOrder} addModeByDefault={fabricationOrderAddModeByDefault} />}
        close
        accept={false}
        cancel={false}
        closeCallback={() => setOpenFabricationOrderDialog(false)}
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={lineOrderRow} resourceType='ORDER_LINE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey='lineOrder'
        columns={columns}
        dataFetch={dataFetch}
        actions={actions}
        multipleSelection
        pageable
        preselectedRecord={preselectedRecord}
        addModeByDefault={addModeByDefault}
        editingRow={(event) => setEditing(event)}
        onEditIndexChanged={(editIndex, editingRow) => {
          setOriginalTargetDate(editingRow?.targetDate)
          setOriginalDeliveryDate(editingRow?.deliveryDate ?? '')
          setEditing(editingRow)
        }}
        sticky={false}
      />
      <SmartDialog
        setOpen={openDeliveryDateDialog}
        message={t_('Do you want to update the delivery date of the associated fabrication orders?')}
        title={createDialogTitle({ color: 'primary', title: t_('Change fabrication orders delivery date'), icon: EditCalendar })}
        close={false}
        accept
        cancel
        cancelCallback={deliveryDateDialogHandlers.cancel}
        acceptCallback={deliveryDateDialogHandlers.accept}
        acceptLabel='YES'
        cancelLabel='NO'
      />
    </SensitiveBoundary>
  )
}

export default LineOrder

LineOrder.propTypes = {
  order: PropTypes.object.isRequired,
  addModeByDefault: PropTypes.bool.isRequired
}
